/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode, useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import ErrorHandler from '@ion/dls/src/components/errorhandler/ErrorHandler';
import IonSnackbar from '@ion/dls/src/notifications/IonSnackbar';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {removeQueries} from '@ion/query/src/utils/helpers';
import ReportsProcessing from '../../sisense/ReportsProcessing';
import DashboardContent, {ANALYTICS_FRAME_CONTENT_ID} from './DashboardContent';
import DashboardDrawer from './DashboardDrawer';
import DashboardDrawerSection from './DashboardDrawerSection';
import HeaderBar from './HeaderBar';
import {editingScheduledReport} from './store';
import {Headertabs} from './types';
import useDashboardManager from './useDashboardManager';

const useStyles = makeStyles((theme) => ({
  root : {
    display       : 'flex',
    height        : '100%',
    width         : '100%',
    flexDirection : 'row',
    overflow      : 'hidden'
  },
  main : {
    display       : 'flex',
    flexDirection : 'column',
    width         : '100%',
    height        : '100%'
  },
  drawerPaper : toggleThemeStyle({
    left                           : 'auto',
    height                         : 'calc(100vh - 80px)',
    [theme.breakpoints.down('sm')] : {
      left   : 0,
      height : '100%'
    }
  }, {
    boxShadow        : `${dlsShadow(1)} !important`,
    borderRadius     : '12px',
    borderRightWidth : '2px'
  }),
  content : {
    flexGrow  : 1,
    padding   : toggleThemeStyle('6px', '24px'),
    position  : 'relative',
    overflowX : 'hidden',
    overflowY : 'auto',
    height    : '100%',
    width     : toggleThemeStyle('99%', '97%')
  },
  navbar : {
    display         : 'flex',
    flexDirection   : 'column',
    height          : '99%',
    overflowX       : 'hidden',
    borderRadius    : toggleThemeStyle('0', '12px'),
    backgroundColor : dlsColor('white')
  },
  desktopSideNav : {
    height                         : 'calc(100vh - 152px)',
    padding                        : toggleThemeStyle('0', '8px 0 8px 0'),
    overflowX                      : 'hidden',
    [theme.breakpoints.down('sm')] : {
      height : '100%'
    }
  },
  mobileToggleBtn : {
    position        : 'fixed',
    bottom          : theme.spacing(2),
    right           : theme.spacing(2),
    zIndex          : theme.zIndex.modal + 1,
    backgroundColor : theme.palette.background.paper
  },
  snackbarLink : {
    color          : dlsColor('white'),
    textDecoration : 'underline',
    cursor         : 'pointer'
  }
}));

interface AnalyticsSnackbarOptions {
  showScheduledReportLink?: boolean;
}

/**
 * Dashboard is the main component that renders the dashboard management interface.
 *
 * It includes:
 * - A responsive drawer (for dashboard list and tabs)
 * - A header tab bar (Recent / Shared)
 * - Dashboard content area
 * - Error handling with retry option
 */
export const Dashboard = ({
  route,
  features,
  active = true,
  isAdmReports = false,
  omitReportFilter = true,
  showScheduledReports = false,
  isSchedulerDirector = false,
  isSchedulerFleetManager = false,
  user
}: {
  route: string,
  features?: GenericObject[],
  active?: boolean,
  isAdmReports?: boolean,
  omitReportFilter?: boolean,
  showScheduledReports?: boolean
  isSchedulerDirector?: boolean
  isSchedulerFleetManager?: boolean
  user?: any
}) => {
  const classes = useStyles();
  const [snackbar, setSnackbar] = useState({
    open                    : false,
    type                    : 'success',
    message                 : '' as ReactNode,
    showScheduledReportLink : false
  });

  const showSnackbar = useCallback(
    (message: ReactNode, type: 'success' | 'error', options: AnalyticsSnackbarOptions = {}) => {
      setSnackbar({
        open                    : true,
        message                 : message,
        type                    : type,
        showScheduledReportLink : Boolean(options.showScheduledReportLink)
      });
    },
    []
  );

  const closeSnack = useCallback(() => {
    setSnackbar({open: false, message: '', type: '', showScheduledReportLink: false});
  }, []);

  const {
    mobileOpen,
    setMobileOpen,
    openCategory,
    selectedCategory,
    selectedDashboard,
    setSelectedDashboard,
    onSavedReportLoad,
    setSideTab,
    setHeaderTab,
    headerTab,
    isLoading,
    isError,
    refetch,
    onRefreshAfterError,
    allDashboardCategories,
    formattedCategories,
    toggleCategory,
    categoryFilter,
    setCategoryFilter
  } = useDashboardManager(features, isAdmReports);

  const goToTab = useCallback((event) => {
    setSnackbar({open: false, message: '', type: '', showScheduledReportLink: false});
    setSelectedDashboard(null);
    setHeaderTab(event, Headertabs.ScheduledReports);
  }, [setHeaderTab, setSelectedDashboard]);

  const dismissLabel = translate('common:dismiss');
  const actionLabel = translate('common:showReport');
  const showScheduledReportLink = (
    snackbar.showScheduledReportLink &&
    headerTab !== Headertabs.ScheduledReports
  );
  const scheduledReportSuccessMessage = (
    <>
      {translate('sisense:analytics.createSuccess.prefix')}&nbsp;
      <a href='#'
        className={classes.snackbarLink}
        onClick={function(event) {
          event.preventDefault();
          removeQueries(QueryKey.SCHEDULED_REPORTS, false);
          goToTab(event);
        }}>
        {translate('sisense:formLabels.title')}
      </a>
      &nbsp;{translate('sisense:analytics.createSuccess.suffix')}
    </>
  );

  const toggleDrawer = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const resetSelection = useCallback(() => {
    const category = selectedDashboard?.categoryName;
    const sideTab = selectedDashboard?.tab;
    !isNil(sideTab) && setSideTab(sideTab);
    category && toggleCategory(category, true, sideTab);
    setSelectedDashboard(null);
    if (headerTab === Headertabs.Recent) {
      const t = setTimeout(() => {
        refetch();
        clearTimeout(t);
      }, 350);
    }
  }, [headerTab, refetch, setSideTab, selectedDashboard]);

  if (!isLoading && isError) {
    return (
      <ErrorHandler onRefresh={onRefreshAfterError}
        errorText={translate('sisense:analytics.errorText')}
        hasError />
    );
  }

  return (
    <div className={classes.root}>
      <DashboardDrawer mobileOpen={mobileOpen}
        onToggle={toggleDrawer}
        drawerClasses={classes.drawerPaper}
        toggleButtonClass={classes.mobileToggleBtn}>
        <DashboardDrawerSection openCategory={openCategory}
          toggleCategory={toggleCategory}
          selectedDashboard={selectedDashboard}
          selectedCategory={selectedCategory}
          setSelectedDashboard={setSelectedDashboard}
          onTabChange={setSideTab}
          resetSelection={resetSelection}
          categories={formattedCategories}
          isLoading={isLoading}
          isAdmReports={isAdmReports}
          showScheduledReports={showScheduledReports}
          classes={classes}/>
      </DashboardDrawer>
      <div className={classes.main}>
        <Render.If condition={!selectedDashboard}>
          <HeaderBar value={headerTab}
            onChange={setHeaderTab}
            showScheduledReports={showScheduledReports}
            isSchedulerFleetManager={isSchedulerFleetManager} />
        </Render.If>
        <div className={classes.content} id={ANALYTICS_FRAME_CONTENT_ID}>
          <DashboardContent selectedDashboard={selectedDashboard}
            sortByDate={headerTab === Headertabs.Recent}
            omitReportFilter={omitReportFilter}
            categories={formattedCategories}
            allCategories={allDashboardCategories}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            selectedCategory={selectedCategory}
            toggleCategory={toggleCategory}
            setSelected={setSelectedDashboard}
            onSavedReportLoad={onSavedReportLoad}
            isLoading={isLoading}
            onShowSnackbar={showSnackbar}
            setSideTab={setSideTab}
            isSchedulerDirector={isSchedulerDirector}
            isSchedulerFleetManager={isSchedulerFleetManager}
            route={route}
            user={user}
            active={active} />
          <IonSnackbar show={snackbar.open}
            type={snackbar.type}
            isIcon={false}
            message={(showScheduledReportLink || selectedDashboard) ? scheduledReportSuccessMessage
              : snackbar.message}
            actionLabel={snackbar.showScheduledReportLink ? undefined : actionLabel}
            customDismissLabel={dismissLabel}
            onAction={goToTab}
            onDismiss={closeSnack}
            onClose={closeSnack}
            autoHideDuration={3000} />
        </div>
      </div>
      <ReportsProcessing />
    </div>
  );
};

export default Dashboard;
